import React, { useState, useRef } from 'react'
import {
  Box,
  Popover,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Spinner,
  IconButton,
  Flex,
  Portal,
  useDisclosure
} from '@chakra-ui/react'
import { 
  FiBell, 
  FiCheck, 
  FiTrash2, 
  FiSettings, 
  FiX,
  FiShare2,
  FiDatabase,
  FiHeart,
  FiAlertCircle,
  FiInfo,
  FiMessageSquare,
  FiCalendar,
  FiClock
} from 'react-icons/fi'
import { Tooltip } from '../ui/Tooltip'
import { useTranslation } from 'react-i18next'
import { useNotifications } from '../../hooks/useNotifications'
import { useActiveProfile } from '../../contexts/ActiveProfileContext'
import { useNavigate } from 'react-router-dom'
import { toaster } from '../ui/toaster-instance'
import type { Notification, NotificationType } from '../../types/notifications'

interface NotificationsDropdownProps {
  profileId?: string
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ profileId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications
  } = useNotifications(undefined)
  const { setActiveProfile } = useActiveProfile()

  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set())
  const [isMarkingRead, setIsMarkingRead] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeletingAll, setIsDeletingAll] = useState(false)
  const [deletingNotificationPath, setDeletingNotificationPath] = useState<string | null>(null)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const { open: isOpen, onOpen, onClose } = useDisclosure()

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.readAt) {
      try {
        await markAsRead(notification.id)
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
        // Continue with navigation even if marking as read fails
      }
    }

    // Handle GeneChat/Message navigation specifically
    if (notification.type === 'genechat_response' || notification.type === 'new_message') {
      if ((notification as any).data?.threadId) {
        navigate(`/chat/${(notification as any).data.threadId}`)
      } else {
        navigate('/chat')
      }
      onClose()
      return
    }

    // Handle navigation based on notification type
    if (notification.actionUrl) {
      // Extract path from actionUrl (handle both full URLs and relative paths)
      let actionPath = notification.actionUrl
      try {
        // If it's a full URL, extract just the path
        const url = new URL(notification.actionUrl)
        actionPath = url.pathname + url.search + url.hash
      } catch {
        // If it's already a relative path, use it as-is
        actionPath = notification.actionUrl
      }
      
      // If the actionUrl points to a legacy profile-scoped path, switch profile and use new route
      const targetProfileId = notification.profileId || (notification as any).data?.profile_id

      if (actionPath.includes('/wellness-goals')) {
        if (targetProfileId) {
          await setActiveProfile(targetProfileId)
        }
        navigate('/wellness-goals')
      } else if (actionPath.startsWith('/profile/') && targetProfileId) {
        await setActiveProfile(targetProfileId)
        navigate('/dashboard')
      } else if ((actionPath.startsWith('/messages') || actionPath.startsWith('/pro/messages')) && targetProfileId) {
        await setActiveProfile(targetProfileId)
        navigate(actionPath)
      } else {
        navigate(actionPath)
      }
    } else {
      // Default navigation based on type
      switch (notification.type) {
        case 'profile_shared':
        case 'profile_share_accepted':
        case 'profile_share_declined':
          navigate('/account#sharing')
          break
        case 'access_request':
          if ((notification as any).data?.inviteToken) {
            navigate(`/accept-invite/${(notification as any).data.inviteToken}`)
          } else {
            navigate('/account#sharing')
          }
          break
        case 'access_revoked':
        case 'collaborator_left':
          navigate('/account#sharing')
          break
        case 'genetic_upload_completed':
        case 'genetic_analysis_completed':
        case 'genetic_report_ready':
          navigate(`/genetic-reports`)
          break
        case 'wellness_goal_reminder':
        case 'health_checkin_due':
          if (notification.profileId) {
            await setActiveProfile(notification.profileId)
          }
          navigate('/wellness-goals')
          break
        case 'goal_progress_shared':
          // Use actionUrl if available, otherwise navigate to wellness goals
          if (notification.actionUrl) {
            if (notification.actionUrl.includes('/wellness-goals')) {
              if (notification.profileId) {
                await setActiveProfile(notification.profileId)
              }
              navigate('/wellness-goals')
            } else {
              navigate(notification.actionUrl)
            }
          } else {
            if (notification.profileId) {
              await setActiveProfile(notification.profileId)
            }
            navigate('/wellness-goals')
          }
          break
        case 'support_update':
          // Support notifications should use actionUrl, but fallback to conversation_id if available
          if (notification.actionUrl) {
            navigate(notification.actionUrl)
          } else if ((notification as any).data?.conversation_id) {
            navigate(`/support/conversation/${(notification as any).data.conversation_id}`)
          } else if ((notification as any).data?.ticket_id) {
            navigate(`/support/ticket/${(notification as any).data.ticket_id}`)
          } else {
            navigate('/support')
          }
          break
        default:
          // Navigate to profile dashboard
          if (notification.profileId) {
            await setActiveProfile(notification.profileId)
          }
          navigate('/dashboard')
      }
    }

    onClose()
  }

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    setIsMarkingRead(true)
    try {
      await markAllAsRead()
    } catch (error) {
      console.error('Error marking all as read:', error)
    } finally {
      setIsMarkingRead(false)
    }
  }

  // Handle delete all
  const handleDeleteAll = async () => {
    setIsDeletingAll(true)
    try {
      await deleteAllNotifications()
    } catch (error) {
      console.error('Error deleting all notifications:', error)
      toaster.create({
        title: t('dashboard.notifications.delete_failed'),
        description: t('dashboard.notifications.delete_failed_description'),
        type: 'error',
        duration: 5000
      })
    } finally {
      setIsDeletingAll(false)
    }
  }

  // Handle delete selected
  const handleDeleteSelected = async () => {
    if (selectedNotifications.size === 0) return

    setIsDeleting(true)
    try {
      const promises = Array.from(selectedNotifications).map(id => deleteNotification(id))
      await Promise.all(promises)
      setSelectedNotifications(new Set())
    } catch (error) {
      console.error('Error deleting notifications:', error)
      toaster.create({
        title: t('dashboard.notifications.delete_failed'),
        description: t('dashboard.notifications.delete_failed_description'),
        type: 'error',
        duration: 5000
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Toggle notification selection
  const toggleNotificationSelection = (notificationId: string) => {
    const newSelected = new Set(selectedNotifications)
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId)
    } else {
      newSelected.add(notificationId)
    }
    setSelectedNotifications(newSelected)
  }

  // Get notification icon based on type
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'profile_shared':
      case 'profile_share_accepted':
      case 'profile_share_declined':
        return <FiShare2 size="16px" />
      case 'genetic_upload_completed':
      case 'genetic_analysis_completed':
      case 'genetic_report_ready':
        return <FiDatabase size="16px" />
      case 'wellness_goal_reminder':
      case 'health_checkin_due':
        return <FiHeart size="16px" />
      case 'medication_reminder':
      case 'appointment_reminder':
        return <FiCalendar size="16px" />
      case 'security_alert':
        return <FiAlertCircle size="16px" />
      case 'genechat_response':
      case 'new_message':
      case 'support_update':
        return <FiMessageSquare size="16px" />
      default:
        return <FiInfo size="16px" />
    }
  }

  // Get notification color based on type
  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'profile_shared':
      case 'profile_share_accepted':
        return 'blue'
      case 'genetic_upload_completed':
      case 'genetic_analysis_completed':
      case 'genetic_report_ready':
        return 'green'
      case 'wellness_goal_reminder':
      case 'health_checkin_due':
        return 'purple'
      case 'security_alert':
        return 'red'
      case 'payment_failed':
      case 'subscription_expiring':
        return 'orange'
      default:
        return 'gray'
    }
  }

  // Format relative time
  const formatRelativeTime = (date: Date) => {
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return t('dashboard.notifications.just_now')
    if (diffInMinutes < 60) return t('dashboard.notifications.minutes_ago', { count: diffInMinutes })
    if (diffInMinutes < 1440) return t('dashboard.notifications.hours_ago', { count: Math.floor(diffInMinutes / 60) })
    return t('dashboard.notifications.days_ago', { count: Math.floor(diffInMinutes / 1440) })
  }

  return (
    <Tooltip label={t('dashboard.navbar.notifications')} placement="bottom">
      <Popover.Root open={isOpen} onOpenChange={(e) => e.open ? onOpen() : onClose()} positioning={{ placement: 'bottom-end' }}>
        <Popover.Trigger asChild>
          <Button
            ref={triggerRef}
            size="sm"
            variant="ghost"
            aria-label={t('dashboard.navbar.notifications')}
            _hover={{ bg: 'wellness.mint' }}
            position="relative"
            onClick={onOpen}
          >
            <FiBell />
            {unreadCount > 0 && (
              <Badge
                position="absolute"
                top="-8px"
                right="-8px"
                colorScheme="red"
                borderRadius="full"
                fontSize="xs"
                minW="20px"
                h="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content w="400px" maxH="600px" overflow="hidden">
            {/* Optional arrow for visual parity with other menus */}
            {/* <Popover.Arrow /> */}
            <Popover.Header>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold">
                {t('dashboard.navbar.notifications')}
                {unreadCount > 0 && (
                  <Badge ml={2} colorScheme="red" variant="subtle">
                    {unreadCount}
                  </Badge>
                )}
              </Text>
              <HStack gap={2}>
                {unreadCount > 0 && (
                  <Tooltip label={t('dashboard.notifications.mark_all_read')} placement="bottom">
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={handleMarkAllAsRead}
                      loading={isMarkingRead}
                      aria-label={t('dashboard.notifications.mark_all_read')}
                    >
                      <FiCheck size="14px" />
                    </Button>
                  </Tooltip>
                )}
                {notifications.length > 0 && (
                  <Tooltip label={t('dashboard.notifications.delete_all')} placement="bottom">
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={handleDeleteAll}
                      loading={isDeletingAll}
                      aria-label={t('dashboard.notifications.delete_all')}
                      colorScheme="red"
                    >
                      <FiTrash2 size="14px" />
                    </Button>
                  </Tooltip>
                )}
                <Tooltip label={t('dashboard.notifications.settings')} placement="bottom">
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label={t('dashboard.notifications.settings')}
                    onClick={() => navigate('/account/settings')}
                  >
                    <FiSettings size="14px" />
                  </IconButton>
                </Tooltip>
              </HStack>
            </Flex>
            </Popover.Header>

            <Popover.Body p={0} maxH="500px" overflowY="auto">
            {isLoading ? (
              <Box p={6} textAlign="center">
                <Spinner size="md" />
                <Text mt={2} fontSize="sm" color={{ base: 'gray.500', _dark: 'gray.400' }}>
                  {t('dashboard.notifications.loading')}
                </Text>
              </Box>
            ) : notifications.length === 0 ? (
              <Box p={6} textAlign="center">
                <FiBell size="32px" color="var(--chakra-colors-gray-400)" />
                <Text mt={2} fontSize="sm" color={{ base: 'gray.500', _dark: 'gray.400' }}>
                  {t('dashboard.notifications.no_notifications')}
                </Text>
              </Box>
            ) : (
              <VStack gap={0} align="stretch">
                {/* Bulk actions */}
                {selectedNotifications.size > 0 && (
                  <Box
                    p={3}
                    bg={{ base: 'gray.50', _dark: 'gray.800' }}
                    borderBottom="1px"
                    borderColor={{ base: 'gray.200', _dark: 'gray.700' }}
                  >
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={{ base: 'gray.600', _dark: 'gray.300' }}>
                        {t('dashboard.notifications.selected_count', { count: selectedNotifications.size })}
                      </Text>
                      <HStack gap={2}>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={handleDeleteSelected}
                          loading={isDeleting}
                          colorScheme="red"
                        >
                          <FiTrash2 size="14px" />
                          {t('dashboard.notifications.delete_selected')}
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setSelectedNotifications(new Set())}
                        >
                          <FiX size="14px" />
                          {t('dashboard.notifications.clear_selection')}
                        </Button>
                      </HStack>
                    </HStack>
                  </Box>
                )}

                {/* Notifications list */}
                {notifications.map((notification, index) => (
                  <Box
                    key={notification.documentPath}
                    p={3}
                    borderBottom="1px"
                    borderColor={{ base: 'gray.100', _dark: 'gray.700' }}
                    bg={
                      notification.readAt
                        ? { base: 'white', _dark: 'gray.800' }
                        : { base: 'blue.50', _dark: 'blue.900' }
                    }
                    cursor="pointer"
                    _hover={{
                      bg: notification.readAt
                        ? { base: 'gray.50', _dark: 'gray.700' }
                        : { base: 'blue.100', _dark: 'blue.800' },
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <HStack gap={3} align="start">
                      {/* Selection checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedNotifications.has(notification.id)}
                        onChange={(e) => {
                          e.stopPropagation()
                          toggleNotificationSelection(notification.id)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />

                      {/* Notification icon */}
                      <Box
                        color={`${getNotificationColor(notification.type)}.500`}
                        mt="2px"
                      >
                        {getNotificationIcon(notification.type)}
                      </Box>

                      {/* Notification content */}
                      <VStack gap={1} align="start" flex={1}>
                        <Text fontSize="sm" fontWeight="medium" lineClamp={2}>
                          {notification.title}
                        </Text>
                        {notification.message && (
                          <Text fontSize="xs" color={{ base: 'gray.600', _dark: 'gray.300' }} lineClamp={2}>
                            {notification.message}
                          </Text>
                        )}
                        {/* Row is clickable; no extra button */}
                        <HStack gap={2} fontSize="xs" color={{ base: 'gray.500', _dark: 'gray.400' }}>
                          <HStack gap={1}>
                            <FiClock size="12px" />
                            <Text>{formatRelativeTime(notification.createdAt)}</Text>
                          </HStack>
                          {notification.priority === 'high' && (
                            <Badge size="sm" colorScheme="red" variant="subtle">
                              {t('dashboard.notifications.high_priority')}
                            </Badge>
                          )}
                          {notification.priority === 'urgent' && (
                            <Badge size="sm" colorScheme="red">
                              {t('dashboard.notifications.urgent_priority')}
                            </Badge>
                          )}
                        </HStack>
                      </VStack>

                      {/* Action buttons */}
                      <HStack gap={1}>
                        {!notification.readAt && (
                          <Tooltip label={t('dashboard.notifications.mark_as_read')} placement="top">
                            <IconButton
                              size="xs"
                              variant="ghost"
                              aria-label={t('dashboard.notifications.mark_as_read')}
                              onClick={async (e) => {
                                e.stopPropagation()
                                try {
                                  await markAsRead(notification.id)
                                } catch (error) {
                                  console.error('Failed to mark notification as read:', error)
                                  // TODO: Show error toast to user
                                }
                              }}
                            >
                              <FiCheck size="12px" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip label={t('dashboard.notifications.delete_notification')} placement="top">
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={t('dashboard.notifications.delete_notification')}
                            loading={deletingNotificationPath === notification.documentPath}
                            onClick={async (e) => {
                              e.stopPropagation()
                              setDeletingNotificationPath(notification.documentPath)
                              try {
                                await deleteNotification(notification.id)
                              } catch (error) {
                                console.error('Failed to delete notification:', error)
                                toaster.create({
                                  title: t('dashboard.notifications.delete_failed'),
                                  description: t('dashboard.notifications.delete_failed_description'),
                                  type: 'error',
                                  duration: 5000
                                })
                              } finally {
                                setDeletingNotificationPath(null)
                              }
                            }}
                          >
                            <FiTrash2 size="12px" />
                          </IconButton>
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
       </Portal>
     </Popover.Root>
    </Tooltip>
  )
}
